import React from "react";

export interface ChannelState {
  idx: number;
  label: string;
  color: string;     // "#RRGGBB"
  on: boolean;
  lo: number;
  hi: number;
  // Color this channel loaded with (canonical → carried pref → manifest). Used
  // only to detect "the reviewer changed the swatch" for markers that have no
  // lab-canonical window yet, so the "set default" affordance can appear.
  def?: string;
}

const FIGURE_TRIPLET = new Set(["Hoechst", "CD105", "FN1"]);

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
const clampLo = (v: number, hi: number) => Number.isFinite(v) ? clamp(v, 0, hi - 1) : 0;
const clampHi = (v: number, lo: number) => Number.isFinite(v) ? clamp(v, lo + 1, 65535) : 65535;

export interface CanonWindow { lo: number; hi: number; color: string | null; }

export function ChannelPanel({channels, onChange, canonical = {}, onSetDefault}: {
  channels: ChannelState[];
  onChange: (next: ChannelState[]) => void;
  // Lab-canonical windows keyed by marker label. Drives the 🔒/✎ badges and
  // the per-row "revert to canonical" affordance.
  canonical?: Record<string, CanonWindow>;
  // Persist the row's current color+window as the shared lab default for every
  // reviewer (POST /windows). Shown only when the row diverges from its default.
  onSetDefault?: (label: string) => void;
}) {
  function update(i: number, patch: Partial<ChannelState>) {
    const next = channels.map((c, j) => j === i ? {...c, ...patch} : c);
    onChange(next);
  }
  function isOff(c: ChannelState): boolean {
    const lw = canonical[c.label];
    if (!lw) return false;
    return c.lo !== lw.lo || c.hi !== lw.hi ||
      (lw.color || "").toLowerCase() !== (c.color || "").toLowerCase();
  }
  // Whether to offer "set as default": if a canonical window exists, when the
  // row is off it; otherwise when the reviewer has changed the swatch from the
  // color it loaded with (so untouched channels stay uncluttered).
  function canSetDefault(c: ChannelState): boolean {
    if (canonical[c.label]) return isOff(c);
    return c.def != null && (c.def || "").toLowerCase() !== (c.color || "").toLowerCase();
  }
  function preset(kind: "figure" | "all" | "none") {
    const next = channels.map(c => ({
      ...c,
      on: kind === "all"   ? true
        : kind === "none"  ? false
        :  /* figure */     FIGURE_TRIPLET.has(c.label),
    }));
    onChange(next);
  }

  return (
    <aside className="channel-pane">
      <h2>Channels</h2>
      {channels.map((c, i) => (
        <div key={c.label} className={`channel-row ${c.on ? "on" : "off"}`}>
          <div className="row-top">
            <label className="swatch" style={{background: c.color}}>
              <input type="color" value={c.color}
                     onChange={e => update(i, {color: e.target.value})} />
            </label>
            <span className="name">{c.label}</span>
            {canonical[c.label] && (
              isOff(c) ? (
                <button className="canon-badge off" title="Off lab-canonical — click to revert"
                        onClick={() => {
                          const lw = canonical[c.label];
                          update(i, {lo: lw.lo, hi: lw.hi, color: lw.color || c.color});
                        }}>✎ off ↺</button>
              ) : (
                <span className="canon-badge on" title="On lab-canonical window">🔒 lab</span>
              )
            )}
            {onSetDefault && canSetDefault(c) && (
              <button className="canon-badge set"
                      title="Save this color + window as the shared default for every reviewer"
                      onClick={() => onSetDefault(c.label)}>⬆ set default</button>
            )}
            <button className={`toggle ${c.on ? "on" : ""}`}
                    onClick={() => update(i, {on: !c.on})} aria-label="toggle" />
          </div>
          <div className="row-contrast">
            <input type="range" min={0} max={65535} step={50} value={c.lo}
                   onChange={e => update(i, {lo: Math.min(+e.target.value, c.hi - 1)})} />
            <input type="range" min={0} max={65535} step={50} value={c.hi}
                   onChange={e => update(i, {hi: Math.max(+e.target.value, c.lo + 1)})} />
            <div className="row-vals">
              <input type="number" className="val-input" min={0} max={65535} step={1}
                     value={c.lo} aria-label={`${c.label} floor`}
                     onChange={e => update(i, {lo: clampLo(+e.target.value, c.hi)})} />
              <input type="number" className="val-input" min={0} max={65535} step={1}
                     value={c.hi} aria-label={`${c.label} ceiling`}
                     onChange={e => update(i, {hi: clampHi(+e.target.value, c.lo)})} />
            </div>
          </div>
        </div>
      ))}
      <div className="presets">
        <button className="btn" onClick={() => preset("figure")}>Figure triplet</button>
        <button className="btn" onClick={() => preset("all")}>All</button>
        <button className="btn" onClick={() => preset("none")}>None</button>
      </div>
    </aside>
  );
}
